import { type KeyboardEvent, useCallback } from 'react';

import {
  useWorkspaceAgentSelectAssetStoreApi,
  type WorkspaceAgentSelectAssetHighlightDirection,
  type WorkspaceAgentSelectAssetStoreState,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetStoreContext';
import { type WorkspaceAgentSelectAssetResults } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/workspaceAgentSelectAssetTypes';

export type WorkspaceAgentSelectAssetSearchKeyDownParams = {
  getHighlightedIndex: (state: WorkspaceAgentSelectAssetStoreState) => number;
  getResults: (state: WorkspaceAgentSelectAssetStoreState) => WorkspaceAgentSelectAssetResults;
  moveHighlight: (direction: WorkspaceAgentSelectAssetHighlightDirection) => void;
  onEscape: (event: KeyboardEvent<HTMLElement>, state: WorkspaceAgentSelectAssetStoreState) => void;
  onHorizontalArrow?: (
    event: KeyboardEvent<HTMLElement>,
    results: WorkspaceAgentSelectAssetResults,
    highlightedIndex: number,
  ) => boolean;
};

export const useWorkspaceAgentSelectAssetSearchKeyDown = ({
  getHighlightedIndex,
  getResults,
  moveHighlight,
  onEscape,
  onHorizontalArrow,
}: WorkspaceAgentSelectAssetSearchKeyDownParams) => {
  const store = useWorkspaceAgentSelectAssetStoreApi();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const state = store.getState();
      const results = getResults(state);
      const optionCount = results.items.length;
      const highlightedIndex = optionCount > 0 ? Math.min(getHighlightedIndex(state), optionCount - 1) : 0;

      if (event.key === 'Escape') {
        onEscape(event, state);
        return;
      }

      if (event.key === 'ArrowDown' && optionCount > 0) {
        event.preventDefault();
        moveHighlight(1);
        return;
      }

      if (event.key === 'ArrowUp' && optionCount > 0) {
        event.preventDefault();
        moveHighlight(-1);
        return;
      }

      if (
        (event.key === 'ArrowLeft' || event.key === 'ArrowRight') &&
        onHorizontalArrow?.(event, results, highlightedIndex)
      ) {
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const hasFailed = results.isError && optionCount === 0;

        if (hasFailed) {
          results.onRetry();
          return;
        }

        const highlightedItem = results.items[highlightedIndex];

        if (highlightedItem) {
          state.selectItemById(highlightedItem.id);
        }
      }
    },
    [getHighlightedIndex, getResults, moveHighlight, onEscape, onHorizontalArrow, store],
  );

  return { handleKeyDown };
};
